import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getFiscalHealth, type FiscalHealthSummary } from '../services/apiService.ts';

// SUPERADMIN-17/18/19 (MA-28c) — cross-tenant fiscal-health tile.
//
// Read-only summary of per-tenant fiscal artifacts (issued SalesDocument counts + e-Factura linkage)
// from GET /api/v1/super/fiscal-health. It answers the operator question "which tenants are actually
// producing fiscal documents, and which are silent?" — the transversal fiscal layer that was
// previously invisible from :3100. No intervention/flip surface here (that stays OWNER-GATE).

const StatTile: React.FC<{ label: string; value: number | string; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Typography variant="overline" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h4" sx={{ mt: 0.5 }}>
      {value}
    </Typography>
    {hint ? (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    ) : null}
  </Paper>
);

const FiscalHealthPage: React.FC = () => {
  const [summary, setSummary] = useState<FiscalHealthSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFiscalHealth();
      setSummary(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Fiscal health could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const totals = summary?.platformTotals;
  const tenants = summary?.tenants ?? [];

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Superadmin / Fiscal
        </Typography>
        <Typography variant="h4">Fiscal Health</Typography>
        <Typography color="text.secondary">
          Cross-tenant rollup of issued fiscal documents (receipts, invoices, credit notes) and
          e-Factura linkage. Read-only &mdash; counts only, no per-document detail and no intervention.
        </Typography>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {summary?.degraded ? (
        <Alert severity="warning">
          The fiscal rollup returned degraded &mdash; counts may be incomplete (backend reported a
          read failure).
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Tenants with documents"
            value={totals?.tenantsWithDocuments ?? 0}
            hint="at least one issued document"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile label="Total documents" value={totals?.totalDocuments ?? 0} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Receipts / Invoices"
            value={`${totals?.receipts ?? 0} / ${totals?.invoices ?? 0}`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="e-Factura linked"
            value={totals?.efacturaLinked ?? 0}
            hint={`${totals?.creditNotes ?? 0} credit notes · ${totals?.canceled ?? 0} canceled`}
          />
        </Grid>
      </Grid>

      <Paper sx={{ overflowX: 'auto' }}>
        <Box sx={{ p: 2, pb: 0 }}>
          <Typography variant="h6">Per-tenant fiscal artifacts</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell align="right">Documents</TableCell>
              <TableCell align="right">Receipts</TableCell>
              <TableCell align="right">Invoices</TableCell>
              <TableCell align="right">Credit notes</TableCell>
              <TableCell align="right">Canceled</TableCell>
              <TableCell align="right">e-Factura</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    No tenant has issued any fiscal document yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tenants.map((tenant) => (
                <TableRow key={tenant.tenantId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {tenant.tenantName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      #{tenant.tenantId}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{tenant.totalDocuments}</TableCell>
                  <TableCell align="right">{tenant.receiptCount}</TableCell>
                  <TableCell align="right">{tenant.invoiceCount}</TableCell>
                  <TableCell align="right">{tenant.creditNoteCount}</TableCell>
                  <TableCell align="right">
                    {tenant.canceledCount > 0 ? (
                      <Chip label={tenant.canceledCount} size="small" color="warning" variant="outlined" />
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell align="right">{tenant.efacturaLinkedCount}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="caption" color="text.secondary">
        Source: accountingops_sales_documents. Fiscal emission itself (real Datecs bon, e-Factura
        submission) remains dormant-by-design and owner-gated; this view only reports what has been
        issued.
      </Typography>
    </Stack>
  );
};

export default FiscalHealthPage;
